import React from "react";
import { MyButton } from "./ButtonUtils";
import { SelectInput } from "./FormUtils";
import { MdChevronLeft, MdChevronRight, MdFirstPage, MdLastPage } from "react-icons/md";

export const Pagination = ({
  page,
  setPage,
  pageSize,
  setPageSize,
  total_items = 8,
  callbackFunction,
}) => {
  total_items = !total_items ? 0 : total_items;

  // Page math (display only)
  const pages = Math.ceil(total_items / pageSize || 1);
  const start = total_items === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = total_items === 0 ? 0 : Math.min(page * pageSize, total_items);

  const canPrev = page > 1;
  const canNext = page < pages;

  // pagination options
  const options = [50, 100, 200, 300, 400, 500, 600, 7000, 800, 900, 1000, 1500, 2000];

  // Check if moving forward is possible (kept from original)
  const ceiling = () => Math.ceil(total_items / pageSize);

  const onSizeChange = (e) => {
    const value = e?.target?.value; // keep original behavior (string ok)
    setPageSize(value);
    setPage(1);
    callbackFunction(1, value);
  };

  const goFirst = () => {
    if (page !== 1) {
      setPage(1);
      callbackFunction(1, pageSize);
    }
  };

  const goPrev = () => {
    if (page - 1 >= 1) {
      setPage(page - 1);
      callbackFunction(page - 1, pageSize);
    }
  };

  const goNext = () => {
    if (page + 1 <= ceiling()) {
      setPage(page + 1);
      callbackFunction(page + 1, pageSize);
    }
  };

  const goLast = () => {
    const last = ceiling();
    if (page !== last) {
      setPage(last);
      callbackFunction(last, pageSize);
    }
  };

  return (
    <div className="mt-4">
      <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 shadow-sm">
        {/* Top row: Page size + counters */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="w-full max-w-[220px]">
            <SelectInput
              title={"Page Size"}
              options={options}
              defaultValue={pageSize}
              onChange={onSizeChange}
            />
          </div>

          <div className="flex flex-col items-start sm:items-end gap-1">
            <div className="text-xs text-gray-600">
              Showing{" "}
              <span className="font-semibold text-gray-900">
                {start.toLocaleString()}–{end.toLocaleString()}
              </span>{" "}
              of <span className="font-semibold text-gray-900">{total_items.toLocaleString()}</span>
            </div>
            <div className="inline-flex items-center gap-2">
              <span className="text-xs uppercase tracking-wide text-gray-600">Page</span>
              <span className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-mono font-semibold text-gray-900 shadow-sm">
                {String(page).padStart(2, "0")} /{" "}
                {pages === 0 ? "01" : String(pages).padStart(2, "0")}
              </span>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="my-4 h-px w-full bg-gray-100" />

        {/* Controls */}
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
          <MyButton
            title={
              <span className="inline-flex items-center gap-2">
                <MdFirstPage className="h-4 w-4" />
                <span>First</span>
              </span>
            }
            disabled={!canPrev}
            className={`px-4 py-2 ${canPrev ? "bg-gray-900 hover:bg-gray-800" : "bg-gray-300"
              }`}
            onClick={goFirst}
          />

          <MyButton
            title={
              <span className="inline-flex items-center gap-2">
                <MdChevronLeft className="h-4 w-4" />
                <span>Back</span>
              </span>
            }
            disabled={!canPrev}
            className={`px-4 py-2 ${canPrev ? "bg-gray-900 hover:bg-gray-800" : "bg-gray-300"
              }`}
            onClick={goPrev}
          />

          <MyButton
            title={
              <span className="inline-flex items-center gap-2">
                <span>Next</span>
                <MdChevronRight className="h-4 w-4" />
              </span>
            }
            disabled={!canNext}
            className={`px-4 py-2 ${canNext ? "bg-gray-900 hover:bg-gray-800" : "bg-gray-300"
              }`}
            onClick={goNext}
          />

          <MyButton
            title={
              <span className="inline-flex items-center gap-2">
                <span>Last</span>
                <MdLastPage className="h-4 w-4" />
              </span>
            }
            disabled={!canNext}
            className={`px-4 py-2 ${canNext ? "bg-gray-900 hover:bg-gray-800" : "bg-gray-300"
              }`}
            onClick={goLast}
          />
        </div>
      </div>
    </div>
  );
};
